import { notify } from '@liniya/client-core';
import { Mic, Send, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Below this a recording is a mis-click, not a message. */
const MINIMUM_MS = 700;

/**
 * The container the browser will actually give us.
 *
 * Chrome and Firefox record Opus in WebM; Safari records AAC in MP4. Asking
 * for one and getting the other silently produces a file the other client
 * cannot play, so the type is read back from the recorder rather than assumed,
 * and the extension follows it.
 */
const PREFERRED = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

/**
 * Recording a voice message in the browser.
 *
 * Click to start, click to send — the same two deliberate taps the phone uses,
 * so a habit formed on one client works on the other.
 */
export function VoiceRecorder({
  disabled,
  onRecorded,
}: {
  disabled: boolean;
  onRecorded: (file: File) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const keep = useRef(true);

  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setElapsedMs(Date.now() - startedAt.current), 200);
    return () => window.clearInterval(timer);
  }, [recording]);

  // Leaving the page mid-recording must not leave the microphone light on.
  useEffect(
    () => () => {
      recorder.current?.stream.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  const begin = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      notify('Браузер не умеет записывать звук.', 'error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = PREFERRED.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
      const instance = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      chunks.current = [];
      keep.current = true;
      instance.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data);
      };
      instance.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const durationMs = Date.now() - startedAt.current;
        const type = instance.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type });
        chunks.current = [];

        if (!keep.current || durationMs < MINIMUM_MS || blob.size === 0) {
          if (keep.current) notify('Запись слишком короткая.');
          return;
        }
        const name = `voice-${Date.now()}.${extensionFor(type)}`;
        onRecorded(new File([blob], name, { type }));
      };

      instance.start();
      recorder.current = instance;
      startedAt.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
    } catch {
      notify('Нужен доступ к микрофону.', 'error');
    }
  };

  const finish = (send: boolean) => {
    keep.current = send;
    setRecording(false);
    recorder.current?.stop();
    recorder.current = null;
  };

  if (!recording) {
    return (
      <button
        className="icon-btn"
        type="button"
        aria-label="Записать голосовое сообщение"
        title="Записать голосовое сообщение"
        disabled={disabled}
        onClick={() => void begin()}
      >
        <Mic size={20} />
      </button>
    );
  }

  const seconds = Math.floor(elapsedMs / 1000);

  return (
    <span className="voice-recording">
      <button
        className="icon-btn"
        type="button"
        aria-label="Отменить запись"
        onClick={() => finish(false)}
      >
        <Trash2 size={18} />
      </button>
      <span className="voice-pulse" aria-hidden="true" />
      <span className="voice-elapsed">
        {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
      </span>
      <button
        className="icon-btn dark"
        type="button"
        aria-label="Отправить голосовое сообщение"
        onClick={() => finish(true)}
      >
        <Send size={18} />
      </button>
    </span>
  );
}
